import { useMemo, useState } from "react";
import type { GfActionKey, GfStage, GfTabProps } from "../../../types/gifting";
import { fetchGfDeal, postGfContacted, postGfSetStage } from "../../../api/giftingClient";
import { ACTION_KEYS, SOURCE_LABEL, STAGE_NAMES, isArchived } from "../../../lib/giftingRules";
import { copyText, GfButton } from "../ui/GfUi";
import { errMsg } from "../gfUtil";
import { CreatorFormModal } from "../modals/CreatorFormModal";
import { CampaignFormModal } from "../modals/CampaignFormModal";
import { AddEntryModal } from "../modals/AddEntryModal";
import { ConfirmModal } from "../modals/ConfirmModal";
import { ArchiveView } from "./pipeline/ArchiveView";
import { BulkBar } from "./pipeline/BulkBar";
import { BulkStageModal } from "./pipeline/BulkStageModal";
import { KanbanBoard } from "./pipeline/KanbanBoard";
import { ListTable } from "./pipeline/ListTable";
import { ListToolbar } from "./pipeline/ListToolbar";
import { TodayChips } from "./pipeline/TodayChips";
import {
  buildRows,
  groupRows,
  matchQuery,
  matchSaved,
  type ListRow,
  type SavedFilterKey,
} from "./pipeline/listModel";
import { buildPipelineCtx } from "./pipeline/model";
import { loadListPrefs, saveListPrefs, type ListPrefs } from "./pipeline/prefs";

type BulkModal = "nudge" | "stage" | null;

/** 案件タブ v2（要対応ファースト）。既定は一覧。上から: タイトル行 → 今日やること → ツールバー → 一括操作 → テーブル */
export function PipelineTab(props: GfTabProps) {
  const { data, nowIso, nav, refresh, toast } = props;
  const { board } = data;
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addIds, setAddIds] = useState<string[]>([]);

  const [prefs, setPrefs] = useState<ListPrefs>(() => loadListPrefs());
  const [q, setQ] = useState("");
  const [saved, setSaved] = useState<SavedFilterKey | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [kbCollapsed, setKbCollapsed] = useState<Record<number, boolean>>({});
  const [modal, setModal] = useState<BulkModal>(null);
  const [busy, setBusy] = useState(false);

  const ctx = useMemo(() => buildPipelineCtx(data, nowIso), [data, nowIso]);
  const live = useMemo(() => board.entries.filter((e) => !isArchived(e)), [board.entries]);
  const archived = useMemo(() => board.entries.filter((e) => isArchived(e)), [board.entries]);
  const allRows = useMemo(() => buildRows(live, ctx), [live, ctx]);

  // 存在しない案件IDがURLに残っていたら「すべて」に倒す
  const campaignFilter =
    nav.state.campaign === "all" || board.campaigns.some((c) => c.id === nav.state.campaign) ? nav.state.campaign : "all";
  const scoped = useMemo(
    () => (campaignFilter === "all" ? allRows : allRows.filter((r) => r.e.campaign_id === campaignFilter)),
    [allRows, campaignFilter]
  );

  const view = nav.state.view;
  const act = nav.state.act;
  const openId = nav.state.sel;

  // 「今日やること」の件数（案件フィルタの範囲・検索/保存フィルタには影響されない）
  const counts = useMemo(() => {
    const c = Object.fromEntries(ACTION_KEYS.map((k) => [k, 0])) as Record<GfActionKey, number>;
    for (const r of scoped) c[r.action.key] += 1;
    return c;
  }, [scoped]);
  const todayTotal = useMemo(() => scoped.filter((r) => r.action.tier < 5).length, [scoped]);

  const rows = useMemo(
    () =>
      scoped.filter(
        (r) => (!act || r.action.key === act) && (!saved || matchSaved(saved, r, nowIso)) && matchQuery(r, q)
      ),
    [scoped, act, saved, q, nowIso]
  );
  const groups = useMemo(() => groupRows(rows, prefs.group, board.campaigns), [rows, prefs.group, board.campaigns]);
  const hasFilter = !!act || !!saved || q.trim() !== "";

  const selRows = useMemo(() => rows.filter((r) => selected.has(r.e.id)), [rows, selected]);
  const allChecked = rows.length > 0 && selRows.length === rows.length;

  const campaignChips = useMemo(
    () => [
      { id: "all", name: "すべて", count: live.length },
      ...board.campaigns
        .filter((c) => c.status === "active" || live.some((e) => e.campaign_id === c.id))
        .map((c) => ({
          id: c.id,
          name: c.name,
          count: live.filter((e) => e.campaign_id === c.id).length,
        })),
    ],
    [board.campaigns, live]
  );

  const clearSel = () => setSelected(new Set());
  const updatePrefs = (patch: Partial<ListPrefs>) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    saveListPrefs(next);
  };

  const onOpen = (id: string) => nav.openEntry(id, "deals");
  // 次のアクションのボタン: パネルを開き、compose で「この作業をする」合図を出す（パネル側が作成欄を開く）。
  // チップの絞り込み（act）は変えない＝ボタンを押しても一覧の並びは動かない
  const onAction = (row: ListRow) => {
    nav.openEntry(row.e.id, "deals");
    nav.set({ compose: row.action.key });
  };
  const onChip = (key: GfActionKey | null) => {
    clearSel();
    nav.set({ act: key, view: view === "archive" ? "list" : view });
  };
  const onClearFilters = () => {
    clearSel();
    setSaved(null);
    setQ("");
    nav.set({ act: null });
  };
  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const toggleAll = () =>
    setSelected((s) => {
      const n = new Set(s);
      if (allChecked) for (const r of rows) n.delete(r.e.id);
      else for (const r of rows) n.add(r.e.id);
      return n;
    });

  // ---- 一括操作 ----
  const runNudge = async () => {
    setBusy(true);
    let ok = 0;
    const failed: string[] = [];
    for (const r of selRows) {
      try {
        await postGfContacted(r.e.id, { kind: r.e.stage === 5 ? "reminder" : "nudge" });
        ok += 1;
      } catch (e) {
        failed.push(`${r.info.creatorName}（${errMsg(e)}）`);
      }
    }
    try {
      await refresh();
    } finally {
      setBusy(false);
      setModal(null);
      clearSel();
    }
    toast(failed.length ? `${ok}件を記録、${failed.length}件は失敗: ${failed.slice(0, 2).join("、")}` : `${ok}件に催促を記録しました`);
  };

  const runCopyUrls = async () => {
    setBusy(true);
    try {
      const res = await Promise.allSettled(selRows.map((r) => fetchGfDeal(r.e.id)));
      const urls: string[] = [];
      let skipped = 0;
      for (const x of res) {
        if (x.status === "fulfilled" && x.value.form_url && x.value.token_revoked !== 1) urls.push(x.value.form_url);
        else skipped += 1;
      }
      if (urls.length === 0) {
        toast("コピーできるフォームURLがありません（未発行または無効）");
        return;
      }
      const ok = await copyText(urls.join("\n"));
      toast(
        ok
          ? `${urls.length}件のフォームURLをコピーしました${skipped ? `（${skipped}件はURLなし・無効のため除外）` : ""}`
          : "クリップボードにコピーできませんでした"
      );
      if (ok) clearSel();
    } finally {
      setBusy(false);
    }
  };

  const runStage = async (stage: GfStage, note: string) => {
    setBusy(true);
    let ok = 0;
    let same = 0;
    const failed: string[] = [];
    for (const r of selRows) {
      if (r.e.stage === stage) {
        same += 1;
        continue;
      }
      try {
        await postGfSetStage(r.e.id, { stage, note });
        ok += 1;
      } catch (e) {
        failed.push(`${r.info.creatorName}（${errMsg(e)}）`);
      }
    }
    try {
      await refresh();
    } finally {
      setBusy(false);
      setModal(null);
      clearSel();
    }
    toast(
      `${ok}件を「${STAGE_NAMES[stage]}」に変更しました${same ? `（${same}件は同じ段階）` : ""}${
        failed.length ? `／${failed.length}件失敗: ${failed.slice(0, 2).join("、")}` : ""
      }`
    );
  };

  const autoEvents = board.recent_auto_events.filter((ev) => ev.source !== "manual");
  const autoText =
    autoEvents.length === 0
      ? "直近24時間の自動更新はありません"
      : `直近24時間に${autoEvents.length}件の段階が自動で進みました`;
  const autoTitle = autoEvents
    .slice(0, 6)
    .map((ev) => `${SOURCE_LABEL[ev.source]} → ${ev.influencer_name}・${STAGE_NAMES[ev.to_stage] ?? ""}`)
    .join("\n");

  return (
    <div className="gf-lv2">
      <div className="gf-lv2-head2">
        <h1 className="gf-h1">案件管理</h1>
        <span className="gf-lv2-autoind" title={autoTitle || undefined}>
          <span className="gf-dot" />
          <b className="gf-lv2-autoind-k">自動更新</b>
          {autoText}
        </span>
        <div className="gf-actions">
          <GfButton onClick={() => setCreatorOpen(true)}>クリエイターを追加</GfButton>
          <GfButton variant="primary" onClick={() => setCampaignOpen(true)}>
            新規案件
          </GfButton>
        </div>
      </div>

      <TodayChips
        counts={counts}
        todayTotal={todayTotal}
        activeTotal={scoped.length}
        waitCount={counts.wait}
        act={act}
        hasFilter={hasFilter}
        onSelect={onChip}
        onClear={onClearFilters}
      />

      <ListToolbar
        view={view}
        onView={(v) => nav.set({ view: v })}
        archivedCount={archived.length}
        q={q}
        onQ={(v) => {
          clearSel();
          setQ(v);
        }}
        campaignChips={campaignChips}
        campaign={campaignFilter}
        onCampaign={(id) => {
          clearSel();
          nav.set({ campaign: id });
        }}
        saved={saved}
        onSaved={(k) => {
          clearSel();
          setSaved(k);
        }}
        group={prefs.group}
        onGroup={(g) => updatePrefs({ group: g })}
        density={prefs.density}
        onDensity={(d) => updatePrefs({ density: d })}
        shown={rows.length}
        pool={scoped.length}
      />

      {view === "list" && selRows.length > 0 && (
        <BulkBar
          count={selRows.length}
          busy={busy}
          onNudge={() => setModal("nudge")}
          onCopyUrls={() => void runCopyUrls()}
          onBack={() => setModal("stage")}
          onClear={clearSel}
        />
      )}

      {view === "list" && (
        <ListTable
          groups={groups}
          grouped={prefs.group !== "none"}
          ctx={ctx}
          density={prefs.density}
          selected={selected}
          openId={openId}
          collapsed={collapsed}
          allChecked={allChecked}
          someChecked={selRows.length > 0}
          total={rows.length}
          onOpen={onOpen}
          onToggle={toggle}
          onToggleAll={toggleAll}
          onToggleGroup={(k) => setCollapsed((c) => ({ ...c, [k]: !c[k] }))}
          onAction={onAction}
        />
      )}
      {view === "kanban" && (
        <KanbanBoard
          rows={rows}
          ctx={ctx}
          collapsed={kbCollapsed}
          openId={openId}
          onToggleCol={(s) => setKbCollapsed((c) => ({ ...c, [s]: !c[s] }))}
          onOpen={onOpen}
        />
      )}
      {view === "archive" && <ArchiveView entries={archived} ctx={ctx} board={board} selectedId={openId} onOpen={onOpen} />}

      <ConfirmModal
        open={modal === "nudge"}
        title={`${selRows.length}件に催促を記録`}
        message="選択した案件に「催促した」と記録します（最終連絡日が今日になります。発送済の案件はリマインドとして記録）。メッセージ自体は送信されません。"
        confirmLabel="記録する"
        busy={busy}
        onConfirm={() => void runNudge()}
        onClose={() => setModal(null)}
      />
      <BulkStageModal
        open={modal === "stage"}
        count={selRows.length}
        busy={busy}
        onConfirm={(s, note) => void runStage(s, note)}
        onClose={() => setModal(null)}
      />

      <CreatorFormModal
        {...props}
        open={creatorOpen}
        onClose={() => setCreatorOpen(false)}
        onSaved={(influencerId, dealId) => {
          // 「Instagramから選ぶ」で案件まで追加済みなら、そのエントリーを開く
          if (dealId) {
            setCreatorOpen(false);
            nav.openEntry(dealId, "deals");
            return;
          }
          // 登録したクリエイターをそのまま案件に追加できるよう、追加ダイアログを続けて開く
          void props.refresh().then(() => {
            setCreatorOpen(false);
            setAddIds([influencerId]);
            setAddOpen(true);
          });
        }}
      />
      <CampaignFormModal
        {...props}
        open={campaignOpen}
        onClose={() => setCampaignOpen(false)}
        onSaved={(campaignId) => nav.set({ campaign: campaignId })}
      />
      <AddEntryModal
        {...props}
        open={addOpen}
        onClose={() => setAddOpen(false)}
        influencerIds={addIds}
        onSaved={() => undefined}
      />
    </div>
  );
}
